import { ArrowUpRight, Brain, FileText } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  EmptyState,
  GlassCard,
  SurfacePageBody,
  SurfacePageHeader,
  SurfacePageLayout,
} from '@/design-system';
import { Commentable } from '@/shared/comments/Commentable';
import { cn } from '@/shared/cn';
import { FIELD_INPUT } from '@/shared/ui-class';
import {
  PROJECT_COLOR_HEX,
  PROJECT_COLOR_LABELS,
  PROJECT_COLORS,
  type ProjectColor,
} from '@/versions/v1/shared/project-colors';
import { projectTileBySlug } from '@/versions/v1/shared/project-seed';
import { projectHomeHref } from '@/versions/v1/shared/project-sidebar-nav';
import { formatRelativeTime } from './format-utils';
import { projectFileCount } from '@/versions/v1/modules/projects/ProjectFiles.transform';

const PROJECT_FILE_CAP_MB = 250;
const MAX_DESCRIPTION = 400;

const SECTION_TITLE =
  'text-base font-medium text-zinc-900 dark:text-zinc-100';

const FIELD_LABEL =
  'block text-sm font-medium text-zinc-700 dark:text-zinc-200';

const VIEW_PROJECT_HOME_CLASS =
  'inline-flex items-center gap-1.5 rounded-lg border border-[var(--glass-border)] bg-[var(--glass-surface)] px-3 py-1.5 text-sm text-zinc-700 transition-colors hover:bg-zinc-900/[0.04] dark:text-zinc-300 dark:hover:bg-white/[0.04]';

function ProjectQuotaMeter({
  usedMb,
  capMb,
}: {
  usedMb: number;
  capMb: number;
}) {
  const pct = capMb === 0 ? 0 : Math.floor((usedMb / capMb) * 100);
  const over = usedMb > capMb;
  const band: 'neutral' | 'amber' | 'red' =
    over || pct >= 95 ? 'red' : pct >= 80 ? 'amber' : 'neutral';
  const barClass =
    band === 'red'
      ? 'bg-red-500 dark:bg-red-400'
      : band === 'amber'
        ? 'bg-amber-500 dark:bg-amber-400'
        : 'bg-zinc-900 dark:bg-zinc-100';
  const labelClass =
    band === 'red'
      ? 'text-red-700 dark:text-red-300'
      : band === 'amber'
        ? 'text-amber-700 dark:text-amber-300'
        : 'text-zinc-600 dark:text-zinc-400';
  const usedLabel =
    usedMb >= 10 ? `${Math.round(usedMb)} MB` : `${usedMb.toFixed(1)} MB`;

  return (
    <div
      className="flex items-center justify-between gap-3 px-1 text-xs"
      role="group"
      aria-label="Project storage quota"
    >
      <div
        className="h-1 flex-1 overflow-hidden rounded-full bg-zinc-100 dark:bg-zinc-800"
        role="progressbar"
        aria-valuenow={Math.min(100, pct)}
        aria-valuemin={0}
        aria-valuemax={100}
      >
        <div
          className={cn('h-full transition-[width] duration-200 ease-out', barClass)}
          style={{ width: `${Math.min(100, pct)}%` }}
        />
      </div>
      <span className={cn('flex-shrink-0 tabular-nums', labelClass)}>
        {usedLabel} / {Math.round(capMb)} MB used
        {over ? ' (over quota)' : ''}
      </span>
    </div>
  );
}

function AboutSection({
  name,
  description,
  color,
  onNameChange,
  onDescriptionChange,
  onColorChange,
}: {
  name: string;
  description: string;
  color: ProjectColor;
  onNameChange: (value: string) => void;
  onDescriptionChange: (value: string) => void;
  onColorChange: (value: ProjectColor) => void;
}) {
  return (
    <section>
      <h2 className={cn(SECTION_TITLE, 'mb-3')}>About this project</h2>
      <GlassCard tone="strong" className="overflow-hidden">
        <div className="space-y-4 p-5">
          <div>
            <label htmlFor="project-name" className={FIELD_LABEL}>
              Name
            </label>
            <input
              id="project-name"
              value={name}
              onChange={(event) => onNameChange(event.target.value)}
              className={cn('mt-1 w-full', FIELD_INPUT)}
            />
          </div>

          <div>
            <label htmlFor="project-description" className={FIELD_LABEL}>
              Description
            </label>
            <p className="mt-1 text-xs text-muted">
              Inherited by every thread in this project as a system-prompt suffix.
              Examples: &quot;Cite primary sources.&quot;, &quot;Outputs go to
              /docs/specs/.&quot;
            </p>
            <textarea
              id="project-description"
              value={description}
              onChange={(event) =>
                onDescriptionChange(event.target.value.slice(0, MAX_DESCRIPTION))
              }
              rows={3}
              className={cn('mt-2 w-full', FIELD_INPUT)}
            />
            <div className="mt-1 text-right text-xs text-zinc-400 dark:text-zinc-500">
              {description.length} / {MAX_DESCRIPTION}
            </div>
          </div>

          <div>
            <span className={FIELD_LABEL}>Color</span>
            <div className="mt-2 flex gap-2">
              {PROJECT_COLORS.map((option) => (
                <button
                  key={option}
                  type="button"
                  onClick={() => onColorChange(option)}
                  aria-label={PROJECT_COLOR_LABELS[option]}
                  aria-pressed={color === option}
                  title={PROJECT_COLOR_LABELS[option]}
                  className={
                    color === option
                      ? 'h-7 w-7 rounded-full ring-2 ring-zinc-900 ring-offset-2 ring-offset-white dark:ring-zinc-100 dark:ring-offset-zinc-950'
                      : 'h-7 w-7 rounded-full ring-1 ring-inset ring-black/10 hover:ring-2 hover:ring-zinc-300'
                  }
                  style={{ backgroundColor: PROJECT_COLOR_HEX[option] }}
                />
              ))}
            </div>
          </div>

          <div className="flex justify-end">
            <button
              type="button"
              className="inline-flex items-center gap-1.5 rounded-lg bg-zinc-900 px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-zinc-800 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-zinc-100 dark:text-zinc-900 dark:hover:bg-white"
            >
              Save changes
            </button>
          </div>
        </div>
      </GlassCard>
    </section>
  );
}

export function ProjectSettingsScene() {
  const { projectId } = useParams<{ projectId: string }>();
  const project = useMemo(() => projectTileBySlug(projectId), [projectId]);
  const [name, setName] = useState(project?.name ?? '');
  const [description, setDescription] = useState(project?.description ?? '');
  const [color, setColor] = useState<ProjectColor>(project?.color ?? 'slate');

  const fileCount = project ? projectFileCount(project) : 0;
  const usedMb = project
    ? Math.round((fileCount * 2.1 + 12) * 10) / 10
    : 0;

  if (!project) {
    return (
      <SurfacePageLayout width="narrow">
        <SurfacePageBody className="space-y-4">
          <SurfacePageHeader title="Project not found" />
          <Link to="/projects" className="text-sm text-accent-600">
            Back to projects
          </Link>
        </SurfacePageBody>
      </SurfacePageLayout>
    );
  }

  return (
    <SurfacePageLayout width="narrow">
      <Commentable name="ProjectSettingsScene">
        <SurfacePageBody className="space-y-8" as="div">
          <SurfacePageHeader
            kicker="Settings"
            title={project.name}
            description={`${project.threadCount} thread${project.threadCount === 1 ? '' : 's'} · ${fileCount} file${fileCount === 1 ? '' : 's'} · created ${formatRelativeTime(project.createdAt)}`}
            actions={
              <Link to={projectHomeHref(project.slug)} className={VIEW_PROJECT_HOME_CLASS}>
                View project home
                <ArrowUpRight className="h-3.5 w-3.5" />
              </Link>
            }
          />

          <AboutSection
            name={name}
            description={description}
            color={color}
            onNameChange={setName}
            onDescriptionChange={setDescription}
            onColorChange={setColor}
          />

          <section>
            <div className="mb-3 flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <h2 className={SECTION_TITLE}>Project memory</h2>
                <p className="mt-1 text-sm text-muted">
                  Facts the agent remembers for threads inside this project. Stacked
                  on top of your global memory (
                  <Link
                    to="/settings/memory"
                    className="underline decoration-zinc-400 underline-offset-2 hover:text-zinc-900 dark:hover:text-zinc-100"
                  >
                    /settings/memory
                  </Link>
                  ) — both lists flow into every prompt here.
                </p>
              </div>
              <button
                type="button"
                className="shrink-0 rounded-lg border border-[var(--glass-border)] bg-[var(--glass-surface)] px-3 py-1.5 text-sm font-medium text-zinc-700 transition-colors hover:bg-zinc-900/[0.04] dark:text-zinc-300 dark:hover:bg-white/[0.04]"
              >
                Add memory
              </button>
            </div>
            <GlassCard tone="strong" className="px-5 py-8">
              <EmptyState
                icon={<Brain className="h-8 w-8 text-zinc-400" />}
                title="No project memories yet"
                description="The agent saves memories here automatically when you're chatting inside this project. Global facts (your role, preferences, anything that applies everywhere) belong under Settings → Memory instead."
              />
            </GlassCard>
          </section>

          <section>
            <h2 className={cn(SECTION_TITLE, 'mb-3')}>Project files</h2>
            <p className="mb-3 text-sm text-muted">
              Text, image, and PDF files the agent can read on demand inside this
              project. Every thread in the project sees a manifest at the top of its
              system prompt; the agent fetches contents via{' '}
              <code className="rounded bg-zinc-100 px-1 text-xs dark:bg-zinc-800">
                read_project_file
              </code>{' '}
              when relevant.
            </p>
            <div className="space-y-4">
              <div className="rounded-lg border border-dashed border-[var(--glass-border)] px-4 py-6 text-center text-sm text-muted">
                Drop files here or{' '}
                <button type="button" className="font-medium text-zinc-900 dark:text-zinc-100">
                  browse
                </button>
              </div>
              {/* The empty state only where the project has no files; the count
                  reads in the header beside the thread count. */}
              {fileCount === 0 ? (
                <GlassCard tone="strong" className="px-5 py-8">
                  <EmptyState
                    icon={<FileText className="h-8 w-8 text-zinc-400" />}
                    title="No files yet"
                    description="Add specs, notes, schemas, or source files. The agent decides which to read for each turn, so a clear file name + description go a long way."
                  />
                </GlassCard>
              ) : null}
              <ProjectQuotaMeter usedMb={usedMb} capMb={PROJECT_FILE_CAP_MB} />
            </div>
          </section>

          <section>
            <h2 className={cn(SECTION_TITLE, 'mb-3')}>Defaults</h2>
            <p className="mb-3 text-sm text-muted">
              Fallback composer settings for threads in this project. A per-thread
              choice always wins; cleared fields fall through to the app default.
            </p>
            <GlassCard tone="strong" className="overflow-hidden">
              <div className="space-y-4 p-5">
                <label className="flex items-center justify-between gap-4 text-sm">
                  <span className="text-zinc-700 dark:text-zinc-200">
                    Permission mode
                  </span>
                  <select className={cn('max-w-[12rem]', FIELD_INPUT)}>
                    <option value="">Use app default</option>
                    <option value="default">Ask</option>
                    <option value="bypassPermissions">Auto</option>
                  </select>
                </label>
                <label className="flex items-center justify-between gap-4 text-sm">
                  <span className="text-zinc-700 dark:text-zinc-200">Model</span>
                  <select className={cn('max-w-[12rem]', FIELD_INPUT)}>
                    <option value="">Use app default</option>
                    <option value="claude-opus-4-8">Claude Opus 4.8</option>
                    <option value="claude-sonnet-4-6">Claude Sonnet 4.6</option>
                  </select>
                </label>
                <div className="flex justify-end">
                  <button
                    type="button"
                    className="inline-flex items-center gap-1.5 rounded-lg bg-zinc-900 px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-zinc-800 dark:bg-zinc-100 dark:text-zinc-900 dark:hover:bg-white"
                  >
                    Save defaults
                  </button>
                </div>
              </div>
            </GlassCard>
          </section>

          <section>
            <h2 className={cn(SECTION_TITLE, 'mb-3')}>Danger zone</h2>
            <GlassCard tone="strong" className="overflow-hidden">
              <div className="flex flex-col gap-3 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-zinc-900 dark:text-zinc-100">
                    Delete project
                  </p>
                  <p className="mt-1 text-sm text-muted">
                    Threads fall back to Recents. Project memories are removed. The
                    action cannot be undone.
                  </p>
                </div>
                <button
                  type="button"
                  className="shrink-0 rounded-lg border border-red-200 px-3 py-2 text-sm font-medium text-red-700 transition-colors hover:bg-red-50 dark:border-red-900/60 dark:text-red-400 dark:hover:bg-red-950/30"
                >
                  Delete project
                </button>
              </div>
            </GlassCard>
          </section>
        </SurfacePageBody>
      </Commentable>
    </SurfacePageLayout>
  );
}
